import moment from 'moment';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import Lightbox from 'react-image-lightbox';
import { Tooltip } from 'react-tooltip';
import { useLazyGetExpenseForReportQuery, useLazyGetExpenseItemByExpenseIdQuery, useLazyGetExpenseItemsByExpenseItemIdQuery } from '../../../services/ExpenseApi';
import { useGetAllDraftInvoicesAdminQuery, useLazyGetInvoiceDetailByIdQuery } from '../../../services/InvoiceDataDraftApi';
import ExpenseDetailModal from '../expenseItems/ExpenseDetailModal';
import { UpdateExpenseItemModal } from '../expenseItems/update-single-Item/UpdateExpenseItemModal';
import AddInvoiceModal from './AddInvoiceModal';
import DeleteInvoiceModal from './DeleteInvoiceModal';
import RetryInvoiceModal from './RetryInvoiceModal';
import SingleItemDetailModal from '../expenseItems/update-single-Item/SingleItemDetailModal';
import heic2any from 'heic2any';
import Pagination from '../../../components/pagination/Pagination';
import RefreshComponent from '../../../components/refreshComponent/RefreshComponent';
import TabularInvoices from './TabularInvoices';
import InvoiceDraftDetail from './InvoiceDraftDetail';
import InvoiceDraftFilterForm from './InvoiceDraftFilterForm';
import InvoiceGridList from './InvoiceGridList';
import { PageTitle } from '../../../_metronic/layout/core'
import { Alert } from 'react-bootstrap'

interface LightboxData {
    imageURL: string;
}

// Custom hook to manage modal visibility and auto-refetch on close
const useModalManager = (refetch: () => void) => {
    const [modals, setModals] = useState({
        addUpdateDepartment: false,
        attachmentDelete: false,
        retryInvoice: false,
        expenseDetail: false,
        updateExpenseItem: false,
        expenseItemDetail: false,
    });

    const prevModals = useRef(modals);

    useEffect(() => {
        const changed = Object.keys(modals).some(
            (key) => modals[key as keyof typeof modals] !== prevModals.current[key as keyof typeof modals]
        );
        if (changed) {
            Object.keys(modals).forEach((key) => {
                const k = key as keyof typeof modals;
                if (prevModals.current[k] && !modals[k]) {
                    refetch();
                }
            });
            prevModals.current = modals;
        }
    }, [modals, refetch]);

    const toggleModal = (key: keyof typeof modals, value?: boolean) => {
        setModals((prev) => ({ ...prev, [key]: value ?? !prev[key] }));
    };

    return { modals, toggleModal };
};

const AllInvoicesAdmin = () => {
    const currentDate = moment().format('MM/DD/YYYY');
    const threeDaysAgo = moment().subtract(7, 'days').format('MM/DD/YYYY');

    // UI state
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxData, setLightboxData] = useState<LightboxData>({ imageURL: '' });
    const [heicPreviewUrls, setHeicPreviewUrls] = useState<{ [key: string]: string }>({});
    const [viewMode, setViewMode] = useState<'grid' | 'tabular'>('tabular');
    const [showFilters, setShowFilters] = useState(true);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
    const [isApplyingFilters, setIsApplyingFilters] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showDetailPanel, setShowDetailPanel] = useState(false);
    const [attachmentDeleteData, setAttachmentDeleteData] = useState<number | null>(null);
    const [invoiceDetailData, setInvoiceDetailData] = useState<any>(null);
    const [invoiceDetailLoading, setInvoiceDetailLoading] = useState(false);

    // Pagination
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Filters
    const [filters, setFilters] = useState({
        filterByStatus: "0",
        filterByStartDate: threeDaysAgo,
        filterByEndDate: currentDate,
        filterByUserIds: "",
        searchTerm: "",
        filterByItemStartDate: "",
        filterByItemEndDate: "",
        filterByItemAmount: "",
        filterByReceiptStartDate: "",
        filterByReceiptEndDate: "",
        filterByReceiptInvoiceNo: "",
        filterByReceiptAmount: "",
        filterByItemSource: 0,
        filterByReceiptSource: 0,
        invoiceDataDraftId: "",
    });

    // API hooks
    const [triggerItemsById, { data: itemsByIdData }] = useLazyGetExpenseItemByExpenseIdQuery();
    const [triggerexpenseReportData, { data: expenseReportData }] = useLazyGetExpenseForReportQuery();
    const [triggerItemById, { data: itemByIdData, isFetching: itemByIdDataFetching }] = useLazyGetExpenseItemsByExpenseItemIdQuery();
    const [triggerInvoiceDetail] = useLazyGetInvoiceDetailByIdQuery();

    const { data, isLoading, refetch } = useGetAllDraftInvoicesAdminQuery({
        pageSize,
        pageNumber: page,
        sortBy: "",
        sortOrder: "",
        searchStr: filters.searchTerm || "",
        receiptInvoiceNo: filters.filterByReceiptInvoiceNo || "",
        userIds: filters.filterByUserIds || "",
        filterByStatus: filters.filterByStatus !== "0" ? parseInt(filters.filterByStatus) : 0,
        startDate: filters.filterByStartDate || "",
        endDate: filters.filterByEndDate || "",
        itemStartDate: filters.filterByItemStartDate || "",
        itemEndDate: filters.filterByItemEndDate || "",
        itemAmount: filters.filterByItemAmount || "",
        receiptStartDate: filters.filterByReceiptStartDate || "",
        receiptEndDate: filters.filterByReceiptEndDate || "",
        receiptAmount: filters.filterByReceiptAmount || "",
        itemSource: filters.filterByItemSource || "",
        receiptSource: filters.filterByReceiptSource || "",
        invoiceDataDraftId: filters.invoiceDataDraftId || "",
    });

    // Modal manager
    const { modals, toggleModal } = useModalManager(refetch);
    const {
        addUpdateDepartment: showAddUpdateDepartmentModal,
        attachmentDelete: showAttachmentDeleteModal,
        retryInvoice: showRetryInvoiceAttachmentModal,
        expenseDetail: showExpenseDetailModal,
        updateExpenseItem: showUpdateExpenseItemModal,
        expenseItemDetail: showExpenseItemDetailModal,
    } = modals;

    // HEIC conversion
    useEffect(() => {
        if (!data?.result?.data) return;
        const heicItems = data.result.data.filter(
            (item: any) =>
                item.fileURL &&
                /\.(heic|heif)$/i.test(item.fileURL) &&
                !heicPreviewUrls[item.invoiceDataDraftId]
        );

        heicItems.forEach(async (item: any) => {
            try {
                const response = await fetch(item.fileURL);
                const blob = await response.blob();
                const convertedBlob = await heic2any({ blob, toType: 'image/jpeg', quality: 0.8 });
                const reader = new FileReader();
                reader.onload = () => {
                    setHeicPreviewUrls((prev) => ({
                        ...prev,
                        [item.invoiceDataDraftId]: reader.result as string,
                    }));
                };
                reader.readAsDataURL(convertedBlob as Blob);
            } catch (error) {
                console.error('HEIC conversion error:', error);
            }
        });
    }, [data, heicPreviewUrls]);

    // Auto-refresh for pending OCR
    useEffect(() => {
        const interval = setInterval(() => {
            if (data?.result?.data?.some((item: any) => item.ocrReadStatus === 0)) {
                refetch();
            }
        }, 15000);
        return () => clearInterval(interval);
    }, [data, refetch]);

    // Handlers
    const handleRefresh = useCallback(() => {
        setIsRefreshing(true);
        refetch().finally(() => setIsRefreshing(false));
    }, [refetch]);

    const openPdfInBrowserOrLightbox = useCallback(
        (attachmentFile: string | undefined) => {
            if (!attachmentFile) return;
            const isPdf = /\.pdf$/i.test(attachmentFile);
            const isMsgEml = /\.(msg|eml)$/i.test(attachmentFile);
            const isHeic = /\.(heic|heif)$/i.test(attachmentFile);

            if (isPdf || isMsgEml) {
                window.open(attachmentFile, '_blank');
            } else if (isHeic) {
                const draftId = data?.result?.data?.find(
                    (item: any) => item.fileURL === attachmentFile
                )?.invoiceDataDraftId;
                if (draftId && heicPreviewUrls[draftId]) {
                    setLightboxData({ imageURL: heicPreviewUrls[draftId] });
                    setLightboxOpen(true);
                } else {
                    window.open(attachmentFile, '_blank');
                }
            } else {
                setLightboxData({ imageURL: attachmentFile });
                setLightboxOpen(true);
            }
        },
        [data, heicPreviewUrls]
    );

    const handleApplyFilters = useCallback(
        (newFilters: any) => {
            setIsApplyingFilters(true);
            setFilters(newFilters);
            setPage(1);
            refetch().finally(() => setIsApplyingFilters(false));
        },
        [refetch]
    );

    const handlePageSizeChange = useCallback((e: any) => {
        setPageSize(parseInt(e.target.value));
        setPage(1);
    }, []);

    const handlePageClick = useCallback((e: any) => setPage(e.selected + 1), []);

    const toggleFilters = useCallback(() => setShowFilters((prev) => !prev), []);

    const handleRowClick = useCallback(
        async (item: any) => {
            const id = item?.invoiceDataDraftId;
            if (!id) return;
            setSelectedInvoiceId(id);
            setInvoiceDetailLoading(true);
            try {
                const result = await triggerInvoiceDetail(id).unwrap();
                setInvoiceDetailData(result?.result || null);
            } catch (error) {
                console.error('Failed to fetch invoice detail:', error);
                setInvoiceDetailData(null);
            } finally {
                setInvoiceDetailLoading(false);
            }
            if (!showDetailPanel) setShowDetailPanel(true);
        },
        [showDetailPanel, triggerInvoiceDetail]
    );

    const handleDelete = useCallback((id: number) => {
        setAttachmentDeleteData(id);
        toggleModal('attachmentDelete', true);
    }, [toggleModal]);

    const handleRetry = useCallback((id: number) => {
        setAttachmentDeleteData(id);
        toggleModal('retryInvoice', true);
    }, [toggleModal]);

    const handleCloseDetail = useCallback(() => {
        setSelectedInvoiceId(null);
        setShowDetailPanel(false);
        setInvoiceDetailData(null);
    }, []);

    const toggleDetailPanel = useCallback(() => {
        const newState = !showDetailPanel;
        setShowDetailPanel(newState);
        if (newState && !selectedInvoiceId) {
            const firstItem = data?.result?.data?.[0];
            if (firstItem) handleRowClick(firstItem);
        }
    }, [showDetailPanel, selectedInvoiceId, data, handleRowClick]);

    // Memoized data
    const invoiceList = useMemo(() => data?.result?.data || [], [data]);
    const totalRecords = data?.result?.totalRecords || 0;
    const totalPages = data?.result?.totalPages || 0;
    const pageNumber = data?.result?.pageNumber || 1;

    // Render helpers
    const renderFilterToggle = () => (
        <div className="text-center py-3">
            <button
                className="btn btn-primary btn-sm px-5 py-2 rounded-pill fw-semibold"
                onClick={toggleFilters}
            >
                <i className="fa fa-sliders-h me-2"></i>
                Show Filters
            </button>
        </div>
    );

    const renderViewModeButtons = () => (
        <div className="d-flex align-items-center ms-3 border rounded overflow-hidden shadow-sm bg-white">
            <button
                className={`btn btn-sm ${viewMode === 'tabular' ? 'btn-primary' : 'btn-outline-secondary'} rounded-0 border-end-0 px-4 py-2`}
                onClick={() => setViewMode('tabular')}
                style={{
                    transition: 'all 0.25s ease',
                    fontWeight: viewMode === 'tabular' ? '600' : '400',
                    transform: viewMode === 'tabular' ? 'scale(1.02)' : 'scale(1)'
                }}
            >
                <i className={`fa fa-list me-2 ${viewMode === 'tabular' ? 'text-white' : 'text-primary'}`}></i>
                List View
            </button>

            <button
                className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'} rounded-0 px-4 py-2`}
                onClick={() => setViewMode('grid')}
                style={{
                    transition: 'all 0.25s ease',
                    fontWeight: viewMode === 'grid' ? '600' : '400',
                    transform: viewMode === 'grid' ? 'scale(1.02)' : 'scale(1)'
                }}
            >
                <i className={`fa fa-th-large me-2 ${viewMode === 'grid' ? 'text-white' : 'text-primary'}`}></i>
                Grid View
            </button>
        </div>
    );

    return (
        <>
            {/* Modals */}
            <ExpenseDetailModal
                show={showExpenseDetailModal}
                handleClose={() => toggleModal('expenseDetail', false)}
                data={expenseReportData?.result}
                expenseItems={itemsByIdData?.result}
                refetch={refetch}
            />
            <UpdateExpenseItemModal
                show={showUpdateExpenseItemModal}
                handleClose={() => toggleModal('updateExpenseItem', false)}
                data={itemByIdData?.result}
                viewOnlyData={true}
            />
            <SingleItemDetailModal
                show={showExpenseItemDetailModal}
                handleClose={() => toggleModal('expenseItemDetail', false)}
                data={itemByIdData?.result}
                viewOnlyData={true}
                isFetching={itemByIdDataFetching}
            />
            <DeleteInvoiceModal
                show={showAttachmentDeleteModal}
                handleClose={() => toggleModal('attachmentDelete', false)}
                data={attachmentDeleteData}
            />
            <RetryInvoiceModal
                show={showRetryInvoiceAttachmentModal}
                handleClose={() => toggleModal('retryInvoice', false)}
                data={attachmentDeleteData}
            />
            <AddInvoiceModal
                show={showAddUpdateDepartmentModal}
                handleClose={() => toggleModal('addUpdateDepartment', false)}
                data={{}}
            />

            <Tooltip id="refreshData" place="top" />
            <Tooltip id="retryInvoice" place="top" />
            <Tooltip id="reason" place="top" />
            <Tooltip id="reportId" place="top" />
            <Tooltip id="lineItem" place="top" />
            <Tooltip id="clickReceipt" place="top" />

            {lightboxOpen && (
                <Lightbox
                    mainSrc={lightboxData.imageURL}
                    onCloseRequest={() => setLightboxOpen(false)}
                    imageCaption="Attachment"
                    enableZoom={true}
                    imagePadding={50}
                />
            )}
            <PageTitle breadcrumbs={[]}>All Receipts</PageTitle>

            <div className="row gx-2 gy-2">
                <div className={showDetailPanel ? 'col-lg-8' : 'col-lg-12'}>
                    <div className="card">
                        <div className="card-header border-0 pt-6 align-items-center">
                            <Alert variant="primary" className="mt-2">
                                <p className='fs-6 mt-2'>View and manage all uploaded receipts. Search receipts by user, tracking ID, vendor, or status, review OCR and expense details, retry failed processing, and monitor Business Central synchronization.</p>
                            </Alert>
                        </div>
                        <div className="card-body pb-5 pt-0">
                            <div className="row">
                                <div className="col-12">
                                    {/* Filters */}
                                    {showFilters ? (
                                        <div>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h5 className="mb-0 d-flex align-items-center">
                                                    <i className="fa fa-filter text-primary me-2"></i>
                                                    Filters
                                                </h5>
                                                <button
                                                    className="btn btn-sm btn-primary px-4 py-2 rounded-pill"
                                                    onClick={toggleFilters}
                                                >
                                                    <i className="fa fa-eye-slash me-2"></i>
                                                    Hide Filters
                                                </button>
                                            </div>
                                            <InvoiceDraftFilterForm
                                                onApplyFilters={handleApplyFilters}
                                                isApplyingFilters={isApplyingFilters}
                                                initialFilters={filters}
                                            />
                                        </div>
                                    ) : (
                                        renderFilterToggle()
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="card-body pt-0 pb-5">
                                <div className="d-flex align-items-center justify-content-between border-top pt-5">
                                    <div className="d-flex align-items-center gap-2">
                                        {renderViewModeButtons()}
                                        <RefreshComponent onClick={handleRefresh} isLoading={isRefreshing} />
                                    </div>
                                </div>
                            </div>

                            {/* Results */}
                            <div className="card-body py-8">
                                <div className="row">
                                    {viewMode === 'grid' ? (
                                        <InvoiceGridList
                                            data={invoiceList}
                                            isLoading={isLoading}
                                            selectedInvoiceId={selectedInvoiceId}
                                            onRowClick={handleRowClick}
                                            onDelete={handleDelete}
                                            onRetry={handleRetry}
                                            onViewFile={openPdfInBrowserOrLightbox}
                                            heicPreviewUrls={heicPreviewUrls}
                                        />
                                    ) : (
                                        <div style={{ overflowX: 'auto' }}>
                                            <TabularInvoices
                                                data={invoiceList}
                                                isLoading={isLoading}
                                                onTriggerExpenseReport={(expenseId: number) =>
                                                    triggerexpenseReportData({ expenseId })
                                                }
                                                onTriggerItemsById={(expenseId: number) =>
                                                    triggerItemsById({ ExpenseId: expenseId })
                                                }
                                                onTriggerItemById={(expenseItemId: number) =>
                                                    triggerItemById({ expenseItemId })
                                                }
                                                setShowExpenseItemDetailModal={(show: boolean) =>
                                                    toggleModal('expenseItemDetail', show)
                                                }
                                                setAttachmentDeleteData={setAttachmentDeleteData}
                                                retryInvoiceHandler={() =>
                                                    toggleModal('retryInvoice', true)
                                                }
                                                expenseDetailHandler={() =>
                                                    toggleModal('expenseDetail', true)
                                                }
                                                onRowClick={handleRowClick}
                                                selectedRowId={selectedInvoiceId}
                                                onDelete={handleDelete} // Pass the delete handler
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Pagination */}
                                {!isLoading && invoiceList.length > 0 && (
                                    <div className="row">
                                        <div className="col-md-12">
                                            <Pagination
                                                totalResult={
                                                    totalRecords === 0
                                                        ? 0
                                                        : (pageNumber - 1) * pageSize + 1
                                                }
                                                toResult={
                                                    totalRecords === 0
                                                        ? 0
                                                        : Math.min(pageNumber * pageSize, totalRecords)
                                                }
                                                ofResult={totalRecords}
                                                onChange={handlePageSizeChange}
                                                pageSize={pageSize}
                                                onPageChange={handlePageClick}
                                                pageCount={totalPages}
                                                currentPage={page}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {showDetailPanel && (
                    <div className="col-lg-4">
                        <InvoiceDraftDetail
                            data={invoiceDetailData}
                            isLoading={invoiceDetailLoading}
                            onClose={handleCloseDetail}
                            onImageClick={openPdfInBrowserOrLightbox}
                            onRetry={() => handleRetry(invoiceDetailData?.invoiceDataDraftId)}
                        />
                    </div>
                )}
            </div>
        </>
    );
};

export default AllInvoicesAdmin;